import React from "react";
import styled from "styled-components";
import DatePicker from "react-datepicker";
import { getMomentDate, formatDate } from "../shared/TimeUtil";
import { Button } from "../sharedComponents/Atoms";
import GroupsDropDown from "../sharedComponents/GroupsDropDown";

import "react-datepicker/dist/react-datepicker.css";

const DropDownContainer = styled.div`
  position: relative;
`;

const GroupsLabel = styled.label`
  cursor: pointer;
  width: 100%;
  font-style: ${props => (props.empty ? "italic" : "normal")};
  color: ${props => (props.empty ? "gray" : "var(--color-grey)")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SaveButton = styled(Button)`
  &:hover {
    color: var(--button-secondary-color);
    border-color: var(--button-secondary-color);
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 10;
`;

const NewConsultantModal = styled.div`
  position: fixed;
  top: 15vh;
  left: 50%;
  transform: translateX(-50%);
  z-index: 11;
  background: white;
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  padding: 2rem;
  width: 28rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.3rem;
  color: var(--color-grey);
`;

const ModalField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const ModalFieldLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-grey);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ModalInput = styled.input`
  font-size: 1rem;
  border: none;
  outline: none;
  background: transparent;
  padding: 0.25rem 0;
  width: 100%;
`;

const ModalSelect = styled.select`
  font-size: 1rem;
  border: none;
  outline: none;
  background: transparent;
  padding: 0.25rem 0;
  width: 100%;
  cursor: pointer;
  color: ${props => (props.$empty ? "gray" : "inherit")};
  appearance: none;
  -webkit-appearance: none;
`;

const ModalDropdownChevron = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-right: 1.5px solid gray;
  border-bottom: 1.5px solid gray;
  transform: rotate(45deg) translateY(-2px);
  flex-shrink: 0;
  margin-right: 2px;
`;

const ModalSelectWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.25rem 0;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const ModalInlineError = styled.div`
  color: red;
  font-size: 0.9rem;
`;

const RequiredMark = styled.span`
  color: red;
  margin-left: 0.2rem;
`;

const ModalRequiredNote = styled.p`
  font-size: 0.8rem;
  color: gray;
  margin: 0;
`;

function NewConsultantForm({
  departments,
  seniorities,
  groups,
  onSave,
  onClose
}) {
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    departmentId: "",
    seniorityId: "",
    startDate: null,
    endDate: null,
    selectedGroupIds: []
  });
  const [error, setError] = React.useState("");
  const [groupOpen, setGroupOpen] = React.useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Navn er obligatorisk.");
      return;
    }
    if (!form.email.trim()) {
      setError("E-post er obligatorisk.");
      return;
    }
    if (!form.startDate) {
      setError("Startdato er obligatorisk.");
      return;
    }
    setError("");
    try {
      await onSave(form);
    } catch (err) {
      setError(err.message || "Kunne ikke lagre konsulent.");
    }
  }

  return (
    <>
      <ModalBackdrop onClick={onClose} />
      <NewConsultantModal onClick={e => e.stopPropagation()}>
        <ModalTitle>Ny konsulent</ModalTitle>
        {error && <ModalInlineError>{error}</ModalInlineError>}
        <ModalField>
          <ModalFieldLabel>
            Navn<RequiredMark>*</RequiredMark>
          </ModalFieldLabel>
          <ModalInput
            placeholder="Ola Nordmann"
            value={form.name}
            name="name"
            onChange={handleChange}
            autoFocus
          />
        </ModalField>
        <ModalField>
          <ModalFieldLabel>
            E-post<RequiredMark>*</RequiredMark>
          </ModalFieldLabel>
          <ModalInput
            placeholder="post@domain.com"
            value={form.email}
            name="email"
            onChange={handleChange}
          />
        </ModalField>
        <ModalField>
          <ModalFieldLabel>Lokasjon</ModalFieldLabel>
          <ModalSelectWrapper>
            <ModalSelect
              name="departmentId"
              onChange={handleChange}
              value={form.departmentId}
              $empty={!form.departmentId}
            >
              <option value="">Velg lokasjon</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </ModalSelect>
            <ModalDropdownChevron />
          </ModalSelectWrapper>
        </ModalField>
        <ModalField>
          <ModalFieldLabel>Tjeneste</ModalFieldLabel>
          <ModalSelectWrapper onClick={() => setGroupOpen(o => !o)}>
            <GroupsLabel
              empty={form.selectedGroupIds.length === 0}
              style={{ flex: 1 }}
            >
              {form.selectedGroupIds.length === 0
                ? "Velg tjeneste"
                : (Array.isArray(groups) ? groups : [])
                    .filter(g => form.selectedGroupIds.includes(g.id))
                    .map(g => g.name)
                    .join(", ")}
            </GroupsLabel>
            <ModalDropdownChevron />
          </ModalSelectWrapper>
          {groupOpen && (
            <DropDownContainer>
              <GroupsDropDown
                groups={Array.isArray(groups) ? groups : []}
                selectedGroups={(Array.isArray(groups)
                  ? groups
                  : []
                ).filter(g => form.selectedGroupIds.includes(g.id))}
                onGroupSelect={id =>
                  setForm(prev => ({
                    ...prev,
                    selectedGroupIds: [...prev.selectedGroupIds, id]
                  }))
                }
                onGroupUnselect={id =>
                  setForm(prev => ({
                    ...prev,
                    selectedGroupIds: prev.selectedGroupIds.filter(
                      gId => gId !== id
                    )
                  }))
                }
                onCloseDropdown={() => setGroupOpen(false)}
              />
            </DropDownContainer>
          )}
        </ModalField>
        <ModalField>
          <ModalFieldLabel>Stillingsnivå</ModalFieldLabel>
          <ModalSelectWrapper>
            <ModalSelect
              name="seniorityId"
              onChange={handleChange}
              value={form.seniorityId}
              $empty={!form.seniorityId}
            >
              <option value="">Velg stillingsnivå</option>
              {seniorities.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </ModalSelect>
            <ModalDropdownChevron />
          </ModalSelectWrapper>
        </ModalField>
        <ModalField>
          <ModalFieldLabel>
            Startdato<RequiredMark>*</RequiredMark>
          </ModalFieldLabel>
          <DatePicker
            className="datepicker-input"
            selected={
              form.startDate ? getMomentDate(form.startDate, "YYYYMMDD") : null
            }
            onChange={date =>
              setForm(prev => ({
                ...prev,
                startDate: date ? formatDate(date, "YYYYMMDD") : null
              }))
            }
            placeholderText="Velg dato"
          />
        </ModalField>
        <ModalField>
          <ModalFieldLabel>Sluttdato</ModalFieldLabel>
          <DatePicker
            className="datepicker-input"
            selected={
              form.endDate ? getMomentDate(form.endDate, "YYYYMMDD") : null
            }
            onChange={date =>
              setForm(prev => ({
                ...prev,
                endDate: date ? formatDate(date, "YYYYMMDD") : null
              }))
            }
            placeholderText="Velg dato"
          />
        </ModalField>
        <ModalRequiredNote>
          <RequiredMark>*</RequiredMark> Obligatorisk felt
        </ModalRequiredNote>
        <ModalButtons>
          <Button onClick={onClose} type="button">
            Avbryt
          </Button>
          <SaveButton onClick={handleSave} type="submit">
            Lagre
          </SaveButton>
        </ModalButtons>
      </NewConsultantModal>
    </>
  );
}

export default NewConsultantForm;
